
import React, { useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkMath from 'remark-math';
import rehypeKatex from 'rehype-katex';
import 'katex/dist/katex.min.css';
import './App.css';

function App() {
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileName, setFileName] = useState('');
  const [problemStatement, setProblemStatement] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [annotatedImage, setAnnotatedImage] = useState('');
  const [gptFeedback, setGptFeedback] = useState("");
  const [loading, setLoading] = useState(false);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setFileName(file.name);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile) {
      setStatusMessage("Please select a file first.");
      return;
    }

    setLoading(true);
    setStatusMessage("");
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('problem_statement', problemStatement || "Analyze this circuit for completeness and accuracy.");

    try {
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setStatusMessage(response.data.message);
      setAnnotatedImage(response.data.annotated_image);
      setGptFeedback(response.data.gpt_feedback);
    } catch (error) {
      console.error(error);
      setStatusMessage("Error uploading image");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="page-container">
      <header className="hero-section">
        <div className="hero-overlay">
          <h1 className="hero-title">Circuit Grader</h1>
          <p className="hero-subtitle">
            Upload your circuit diagram and get an instant professional grading report.
          </p>
        </div>
      </header>

      <main className="main-content">
        <div className="upload-card">
          <h2 className="card-title">Upload Your Circuit</h2>
          <p className="card-description">
            Select an image file and describe your circuit below before clicking "Upload & Grade".
          </p>

          <textarea
            className="problem-input"
            placeholder="Enter your problem statement..."
            rows="4"
            value={problemStatement}
            onChange={(e) => setProblemStatement(e.target.value)}
          ></textarea>

          <label className="custom-file-upload">
            <input
              type="file"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            Choose File
          </label>

          {fileName && <span className="file-name">{fileName}</span>}

          <button className="upload-button" onClick={handleUpload}>
            Upload &amp; Grade
          </button>

          {statusMessage && <p className="status-message">{statusMessage}</p>}
        </div>

        {loading && (
          <div className="loader-container">
            <div className="loader"></div>
            <p>Loading, please wait...</p>
          </div>
        )}

        {annotatedImage && !loading && (
          <div className="result-section">
            <h3>Annotated Image</h3>
            <img
              className="annotated-image"
              src={annotatedImage}
              alt="Annotated Circuit"
            />
          </div>
        )}

        {gptFeedback && !loading && (
          <div className="feedback-section">
            <h3>AI-Powered Circuit Grading Report</h3>
            <div className="gpt-feedback">
              <ReactMarkdown
                remarkPlugins={[remarkMath]}
                rehypePlugins={[rehypeKatex]}
              >
                {gptFeedback}
              </ReactMarkdown>
            </div>
          </div>
        )}

        <div className="info-section">
          <h2>How to Use the Circuit Sketch Evaluation Tool</h2>
          <p>
            Welcome to the AI-based Circuit Sketch Evaluation Tool developed by the Austin School for the Driven. This tool enables students, educators, and hobbyists to upload hand-drawn circuit diagrams and receive immediate feedback on their accuracy and design correctness.
          </p>
          <h3>Tool Purpose</h3>
          <p>
            This tool uses AI-based shape recognition and GPT-based analysis to evaluate hand-drawn electrical circuits. Developed during a capstone project, it expands a prior MVP with full component detection and grading logic.
          </p>
          <h3>What You Will Need</h3>
          <ul>
            <li>A clearly drawn circuit sketch on paper or whiteboard.</li>
            <li>A photo or scan of the sketch (JPEG/PNG).</li>
            <li>An optional problem statement (e.g., “Create a series circuit with a 9V battery and two resistors”).</li>
          </ul>
          <h3>How to Use</h3>
          <ol>
            <li>Upload your image file in .jpg/.png format.</li>
            <li>Optionally enter your problem statement.</li>
            <li>Click "Upload & Grade".</li>
            <li>Review annotated image, detected components, and feedback from GPT.</li>
          </ol>
          <h3>Who Can Use It?</h3>
          <ul>
            <li><b>Students:</b> Practice and verify their sketches.</li>
            <li><b>Teachers:</b> Automatically grade submissions.</li>
            <li><b>STEM Mentors:</b> Validate designs before prototyping.</li>
          </ul>
          <h3>Features</h3>
          <ul>
            <li>Recognizes resistors, capacitors, batteries, etc.</li>
            <li>Validates connectivity and completeness.</li>
            <li>Explains issues using GPT-based logic.</li>
            <li>Integration-ready and open-source.</li>
          </ul>
          <h3>Limitations</h3>
          <ul>
            <li>Currently limited to basic topologies (no ICs or logic gates).</li>
            <li>Accuracy depends on image quality.</li>
	    <li>GPT may incorrectly consider an open circuit as closed; manual review of connectivity is advised for accurate evaluation.</li>
          </ul>
          <h3>Open Source</h3>
          <p>
            Released under MIT License.<br/>
            - YOLOv8: <a href="https://github.com/ultralytics/ultralytics" target="_blank">Ultralytics YOLOv8</a><br/>
            - GPT-4 Turbo: <a href="https://platform.openai.com/docs" target="_blank">OpenAI API</a><br/>
            - Flask: <a href="https://flask.palletsprojects.com/" target="_blank">Flask Documentation</a><br/>
            Developed by UT Austin Capstone Team 5 (Spring 2025)<br/>
	  	- Anshul Joshi<br/>
	  	- Anurag Sharma<br/>
	  	- Adam Weintraub<br/>
	  	- Brando Lezzana<br/>
	  	- Nikita Andhale<br/>
	  	- Shanthini D<br/>
          </p>
          <h3>Contact</h3>
          <p>Email: <a href="mailto:founder@austinschoolforthedriven.com">founder@austinschoolforthedriven.com</a></p>
        </div>
      </main>

      <footer className="site-footer">
        <p>&copy; {new Date().getFullYear()} Genesis Creative Collective Inspired</p>
      </footer>
    </div>
  );
}

export default App;

